import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Header, Sidebar } from '@/components';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface DashboardLayoutProps {
  children: React.ReactNode;
  currentView?: string;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  children,
  currentView = 'dashboard',
}) => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false); // Will be used for mobile detection in the future

  const handleLogout = () => {
    logout();
    navigate('/sign-in');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth >= 1024) {
        setMobileMenuOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div className="flex h-screen max-w-screen bg-background">
      {/* Mobile Hamburger Menu Button */}
      <Button
        variant="ghost"
        size="sm"
        className="fixed top-3 left-4 z-[30] lg:hidden"
        onClick={toggleMobileMenu}
      >
        {mobileMenuOpen ? (
          <X className="h-5 w-5" />
        ) : (
          <Menu className="h-5 w-5" />
        )}
      </Button>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`inset-y-0 left-0 z-30 ${isMobile ? (mobileMenuOpen ? 'translate-x-0' : '-translate-x-full') : 'translate-x-0'} transition-transform duration-300`}
      >
        <Sidebar
          collapsed={isCollapsed}
          onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
          onSignOut={handleLogout}
          isMobile={isMobile}
          mobileMenuOpen={mobileMenuOpen}
          onMobileMenuClose={() => setMobileMenuOpen(false)}
          userInfo={
            user
              ? {
                  name:
                    `${user.firstName} ${user.lastName}`.trim() || user.email,
                  email: user.username,
                }
              : undefined
          }
        />
      </div>

      {/* Main Content */}
      <div
        className={`flex-1 flex flex-col h-full transition-all duration-300 ${
          isMobile ? 'ml-0' : isCollapsed ? 'lg:ml-16' : 'lg:ml-64'
        }`}
      >
        {/* Header */}
        <Header
          currentView={currentView}
          userInfo={
            user
              ? {
                  name:
                    `${user.firstName} ${user.lastName}`.trim() || user.email,
                  email: user.username,
                }
              : undefined
          }
          onSignOut={handleLogout}
          isMobile={isMobile}
        />

        {/* Content */}
        <div className="flex-1 max-w-screen overflow-auto p-4 lg:p-6">
          {children}
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
